import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator as CalcIcon, Save, Share2, User, LogIn, FileText, HelpCircle } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import AdBanner from "../components/AdBanner";
import { 
  formatCurrency, 
  parseFormula, 
  calculateSPA, 
  calculateNettDownPayment,
  generateWhatsAppMessage,
  saveToLocalStorage,
  loadFromLocalStorage
} from "../utils/calculations";
import { sharePDF } from "../utils/pdfGenerator";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calculator = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  
  const [grossPrice, setGrossPrice] = useState('');
  const [isBumi, setIsBumi] = useState(false);
  const [bumiPercentage, setBumiPercentage] = useState(7);
  const [formulaString, setFormulaString] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [dpPercentage, setDpPercentage] = useState(10);
  const [isChainLogic, setIsChainLogic] = useState(false);
  
  const [results, setResults] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      }
    };
    checkAuth();

    // Check for template to load
    const templateToLoad = loadFromLocalStorage('load_template');
    if (templateToLoad) {
      setGrossPrice(templateToLoad.grossPrice?.toString() || '');
      setIsBumi(templateToLoad.isBumi || false);
      setBumiPercentage(templateToLoad.bumiPercentage || 7);
      setFormulaString(templateToLoad.formulaString || '');
      setShowAdvanced(templateToLoad.showAdvanced || false);
      setDpPercentage(templateToLoad.dpPercentage || 10);
      setIsChainLogic(templateToLoad.isChainLogic || false);
      localStorage.removeItem('load_template'); // Clear after loading
      return; // Don't load last calculation if template was loaded
    }

    const saved = loadFromLocalStorage('last_calculation');
    if (saved) {
      setGrossPrice(saved.grossPrice?.toString() || '');
      setIsBumi(saved.isBumi || false);
      setBumiPercentage(saved.bumiPercentage || 7);
      setFormulaString(saved.formulaString || '');
      setShowAdvanced(saved.showAdvanced || false);
      setDpPercentage(saved.dpPercentage || 10);
      setIsChainLogic(saved.isChainLogic || false);
    }
  }, []);

  useEffect(() => {
    if (grossPrice && !isNaN(parseFloat(grossPrice))) {
      const gross = parseFloat(grossPrice);
      const spa = calculateSPA(gross, isBumi, bumiPercentage);
      const { rebates, cashbacks, totalRebate, totalCashback } = parseFormula(formulaString, spa, isChainLogic);
      const nett = spa - totalRebate - totalCashback;
      const downPayment = showAdvanced ? calculateNettDownPayment(spa, totalRebate, totalCashback, dpPercentage) : null;

      const calculationResults = {
        grossPrice: gross,
        isBumi,
        bumiPercentage,
        spaPrice: spa,
        rebates,
        cashbacks,
        totalRebate,
        totalCashback,
        nettPrice: nett,
        nettDownPayment: downPayment,
        formulaString,
        showAdvanced,
        dpPercentage,
        isChainLogic
      };

      setResults(calculationResults);
      saveToLocalStorage('last_calculation', calculationResults);
    } else {
      setResults(null);
    }
  }, [grossPrice, isBumi, bumiPercentage, formulaString, showAdvanced, dpPercentage, isChainLogic]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSaveProject = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to save rebate settings");
      return;
    }

    if (!projectName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    if (!formulaString.trim()) {
      toast.error("Please enter a rebate formula");
      return;
    }

    try {
      await axios.post(
        `${API}/projects`,
        {
          name: projectName,
          gross_price: grossPrice ? parseFloat(grossPrice) : null,
          is_bumi: isBumi,
          bumi_percentage: bumiPercentage,
          formula_string: formulaString,
          dp_percentage: dpPercentage,
          show_advanced: showAdvanced
        },
        { withCredentials: true }
      );
      
      toast.success("Rebate template saved!");
      setProjectName('');
    } catch (error) {
      console.error('Save failed:', error);
      toast.error("Failed to save template");
    }
  };

  const handleShare = () => {
    if (!results) {
      toast.error("Please complete the calculation first");
      return;
    }

    const message = generateWhatsAppMessage({
      name: projectName,
      ...results
    });
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleSharePDF = async () => {
    if (!results) {
      toast.error("Please complete the calculation first");
      return;
    }

    try {
      const filename = projectName 
        ? `${projectName.replace(/\s+/g, '-')}-calculation.pdf`
        : 'rebate-calculation.pdf';

      const result = await sharePDF({
        name: projectName || 'Property Calculation',
        ...results
      }, filename);

      if (result.success) {
        if (result.method === 'download') {
          toast.success("PDF downloaded successfully!");
        } else {
          toast.success("PDF shared successfully!");
        }
      } else {
        toast.error(result.error || "Failed to share PDF");
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] pb-[70px]">
      <div className="max-w-md mx-auto p-5 space-y-6">
        <div className="relative pt-8">
          <div 
            className="absolute inset-0 -top-20 opacity-30 pointer-events-none"
            style={{
              background: 'radial-gradient(circle at 50% 0%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)'
            }}
          />
          
          <div className="relative flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <CalcIcon className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-zinc-100">RebatePro</h1>
                <p className="text-sm text-zinc-500">Property Deal Calculator</p>
              </div>
            </div>
            
            {isAuthenticated ? (
              <Button
                data-testid="goto-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                size="sm"
                className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
              >
                <User className="w-4 h-4 mr-2" />
                Templates
              </Button>
            ) : (
              <Button
                data-testid="login-btn"
                onClick={handleLogin}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
            )}
          </div>

          <div className="space-y-5">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2 block">
                  Gross Price
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 font-mono text-xl">RM</span>
                  <input
                    data-testid="gross-price-input"
                    type="number"
                    value={grossPrice}
                    onChange={(e) => setGrossPrice(e.target.value)}
                    placeholder="610000"
                    className="flex-1 bg-transparent border-none text-2xl font-mono text-zinc-100 text-right focus:outline-none focus:ring-0 calc-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Bumi Status</label>
                  <p className="text-xs text-zinc-500">Bumiputera discount</p>
                </div>
                <Switch
                  data-testid="bumi-toggle"
                  checked={isBumi}
                  onCheckedChange={setIsBumi}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>

              {isBumi && (
                <div className="bg-zinc-900/80 rounded-lg p-3 -mt-2 mb-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                      Bumi Discount %
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        data-testid="bumi-percentage-input"
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={bumiPercentage}
                        onChange={(e) => {
                          const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                          setBumiPercentage(val);
                        }}
                        className="w-16 bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-sm font-mono text-zinc-100 text-center focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-zinc-500">%</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      data-testid="bumi-preset-7-btn"
                      onClick={() => setBumiPercentage(7)}
                      size="sm"
                      variant={bumiPercentage === 7 ? "default" : "outline"}
                      className={bumiPercentage === 7 
                        ? "text-xs bg-emerald-500 hover:bg-emerald-400 text-white" 
                        : "text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                      }
                    >
                      Standard (7%)
                    </Button>
                    <Button
                      data-testid="bumi-preset-10-btn"
                      onClick={() => setBumiPercentage(10)}
                      size="sm"
                      variant={bumiPercentage === 10 ? "default" : "outline"}
                      className={bumiPercentage === 10 
                        ? "text-xs bg-emerald-500 hover:bg-emerald-400 text-white" 
                        : "text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                      }
                    >
                      Premium (10%)
                    </Button>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2 block">
                  Formula (e.g., 10+5+20000)
                </label>
                <input
                  data-testid="formula-input"
                  type="text"
                  value={formulaString}
                  onChange={(e) => setFormulaString(e.target.value)}
                  placeholder="10+5+20000"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-lg font-mono text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                />
                <p className="text-xs text-zinc-600 mt-1">Percentages = Rebate, Numbers = Cashback</p>
              </div>

              <div className="flex items-center justify-between py-2 border-t border-zinc-800">
                <div>
                  <label className="text-sm font-semibold text-zinc-300">Advanced Mode</label>
                  <p className="text-xs text-zinc-500">Dynamic down payment</p>
                </div>
                <Switch
                  data-testid="advanced-toggle"
                  checked={showAdvanced}
                  onCheckedChange={setShowAdvanced}
                  className="data-[state=checked]:bg-emerald-500"
                />
              </div>

              {showAdvanced && (
                <div className="space-y-4 pt-4 border-t border-zinc-800">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-semibold text-zinc-300">
                        Down Payment %
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          data-testid="dp-percentage-input"
                          type="number"
                          min="0"
                          max="100"
                          value={dpPercentage}
                          onChange={(e) => {
                            const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                            setDpPercentage(val);
                          }}
                          className="w-16 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm font-mono text-zinc-100 text-center focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                        <span className="text-sm text-zinc-500">%</span>
                      </div>
                    </div>
                    
                    <input
                      data-testid="dp-percentage-slider"
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={dpPercentage}
                      onChange={(e) => setDpPercentage(parseFloat(e.target.value))}
                      className="w-full h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                    />
                    
                    <div className="flex gap-2 mt-3">
                      <Button
                        data-testid="preset-10-btn"
                        onClick={() => setDpPercentage(10)}
                        size="sm"
                        variant={dpPercentage === 10 ? "default" : "outline"}
                        className={dpPercentage === 10 
                          ? "text-xs bg-emerald-500 hover:bg-emerald-400 text-white" 
                          : "text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                        }
                      >
                        Standard (10%)
                      </Button>
                      <Button
                        data-testid="preset-30-btn"
                        onClick={() => setDpPercentage(30)}
                        size="sm"
                        variant={dpPercentage === 30 ? "default" : "outline"}
                        className={dpPercentage === 30 
                          ? "text-xs bg-emerald-500 hover:bg-emerald-400 text-white" 
                          : "text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                        }
                      >
                        3rd Home (30%)
                      </Button>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-zinc-300">
                          Rebate Logic
                        </label>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button 
                                className="w-4 h-4 rounded-full bg-zinc-700/50 flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                                type="button"
                              >
                                <HelpCircle className="w-3 h-3 text-zinc-300" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent 
                              side="top" 
                              className="max-w-xs bg-zinc-900 border-2 border-zinc-700 text-zinc-100 p-4"
                            >
                              <div className="space-y-2">
                                <p className="font-bold text-emerald-400 text-sm">Rebate Calculation Logic</p>
                                <div className="space-y-2 text-xs">
                                  <div>
                                    <p className="font-semibold text-zinc-200">Flat (Simple):</p>
                                    <p className="text-zinc-400">All percentages calculated from base SPA Price. (e.g., 10% + 5% = 15% total of SPA)</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-zinc-200">Chain (Progressive):</p>
                                    <p className="text-zinc-400">Each percentage from remaining balance after previous discount. (e.g., 10% off SPA, then 5% off reduced amount)</p>
                                  </div>
                                </div>
                                <p className="text-[10px] text-zinc-500 italic border-t border-zinc-800 pt-2">
                                  Flat is standard for most Malaysian developers. Use Chain only if specified in sales package.
                                </p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        data-testid="logic-flat-btn"
                        onClick={() => setIsChainLogic(false)}
                        size="sm"
                        variant={!isChainLogic ? "default" : "outline"}
                        className={!isChainLogic 
                          ? "flex-1 text-xs bg-emerald-500 hover:bg-emerald-400 text-white" 
                          : "flex-1 text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                        }
                      >
                        <span className="flex items-center gap-1">
                          Flat
                        </span>
                      </Button>
                      <Button
                        data-testid="logic-chain-btn"
                        onClick={() => setIsChainLogic(true)}
                        size="sm"
                        variant={isChainLogic ? "default" : "outline"}
                        className={isChainLogic 
                          ? "flex-1 text-xs bg-emerald-500 hover:bg-emerald-400 text-white" 
                          : "flex-1 text-xs border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"
                        }
                      >
                        <span className="flex items-center gap-1">
                          Chain
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {results && (
              <div className="space-y-3" data-testid="results-section">
                <div className="bg-zinc-900 border border-zinc-800 rounded-t-xl p-5 result-card">
                  <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1">
                    SPA Price {isBumi && `(After ${bumiPercentage}% Bumi)`}
                  </p>
                  <p className="text-2xl font-bold font-mono text-zinc-100">
                    {formatCurrency(results.spaPrice)}
                  </p>
                </div>

                {results.rebates.length > 0 && (
                  <div className="bg-zinc-900 border-x border-zinc-800 p-5 result-card">
                    <p className="text-xs font-semibold uppercase tracking-widest text-emerald-500 mb-2">
                      Rebates {isChainLogic && '(Chain)'}
                    </p>
                    <div className="space-y-1">
                      {results.rebates.map((rebate, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-zinc-400">{rebate.percentage}%</span>
                          <span className="font-mono text-emerald-400">
                            -{formatCurrency(rebate.amount)}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-zinc-800">
                        <span className="text-zinc-300">Total Rebates</span>
                        <span className="font-mono text-emerald-400">
                          -{formatCurrency(results.totalRebate)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-zinc-900 border-x border-zinc-800 p-5 result-card">
                  <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1">
                    Nett Price (After Rebates)
                  </p>
                  <p className="text-2xl font-bold font-mono text-zinc-100">
                    {formatCurrency(results.spaPrice - results.totalRebate)}
                  </p>
                </div>

                {results.cashbacks.length > 0 && (
                  <div className="bg-zinc-900 border-x border-zinc-800 p-5 result-card">
                    <p className="text-xs font-semibold uppercase tracking-widest text-emerald-500 mb-2">
                      Cashback
                    </p>
                    <div className="space-y-1">
                      {results.cashbacks.map((cashback, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-zinc-400">Flat Amount</span>
                          <span className="font-mono text-emerald-400">
                            -{formatCurrency(cashback)}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-zinc-800">
                        <span className="text-zinc-300">Total Cashback</span>
                        <span className="font-mono text-emerald-400">
                          -{formatCurrency(results.totalCashback)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-zinc-900 border-x border-zinc-800 p-5 result-card">
                  <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-1">
                    Final Price (After Cashback)
                  </p>
                  <p className="text-3xl font-bold font-mono text-zinc-100">
                    {formatCurrency(results.nettPrice)}
                  </p>
                </div>

                {showAdvanced && results.nettDownPayment !== null && (
                  <div 
                    className={`border-2 rounded-b-xl p-6 result-card glow-effect ${
                      results.nettDownPayment >= 0 
                        ? 'bg-gradient-to-br from-orange-500/10 to-red-600/5 border-orange-500/30' 
                        : 'bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/30'
                    }`}
                    data-testid="down-payment-card"
                  >
                    <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${
                      results.nettDownPayment >= 0 ? 'text-orange-400' : 'text-emerald-400'
                    }`}>
                      {results.nettDownPayment >= 0 ? 'Actual Cash Upfront' : 'Cashback to Buyer'}
                    </p>
                    <p className={`text-4xl font-bold font-mono ${
                      results.nettDownPayment >= 0 ? 'text-orange-400' : 'text-emerald-400'
                    }`}>
                      {results.nettDownPayment >= 0 
                        ? formatCurrency(results.nettDownPayment)
                        : formatCurrency(Math.abs(results.nettDownPayment))
                      }
                    </p>
                    <p className="text-xs text-zinc-500 mt-2">
                      = (SPA × {dpPercentage}%) - Rebates - Cashback
                    </p>
                  </div>
                )}

                {isAuthenticated && (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                    <label className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2 block">
                      Save as Rebate Template
                    </label>
                    <input
                      data-testid="project-name-input"
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      placeholder="Template name (e.g., Standard Deal, Luxury Condo)"
                      className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 mb-3"
                    />
                    <Button
                      data-testid="save-project-btn"
                      onClick={handleSaveProject}
                      className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold shadow-lg transition-all active:scale-95"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save Template
                    </Button>
                    <p className="text-xs text-zinc-600 mt-2 text-center">
                      Save rebate formula & settings for quick reuse
                    </p>
                  </div>
                )}

                <Button
                  data-testid="share-whatsapp-btn"
                  onClick={handleShare}
                  className="w-full rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold transition-all active:scale-95 share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share on WhatsApp
                </Button>

                <Button
                  data-testid="share-pdf-btn"
                  onClick={handleSharePDF}
                  className="w-full rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold transition-all active:scale-95"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Share as PDF
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <AdBanner />
    </div>
  );
};

export default Calculator;